/**
 * Wave9 Quote Portal — pricing engine.
 *
 * This is the commercial heart of the portal and the single place the mode rules
 * live. The database trigger in db/migrations/001_init.sql enforces the same
 * rules at write time; if you change one, change both.
 *
 * Rules (spec §2):
 *   buy_price = list_price x (1 - base_discount_pct x tier.multiplier)   [decision 6]
 *   agent     -> sell is LOCKED to list; earns a one-off referral fee    [2, 18]
 *   reseller  -> sell editable, floor at buy price, no upper cap         [2]
 *   internal  -> fully editable, no floor, ad-hoc lines allowed          [27, 29]
 *   recurring -> bills ANNUALLY; terms 12 / 36 / 60 months               [31]
 *
 * ⚠️ Everything here operates on FROZEN values once a quote exists. Call it when
 * building a quote; never re-run it against live catalogue data for a sent quote.
 */

import { round2, lineTotal } from './money.js'
import type {
  CatalogueProduct, PartnerMode, Partner, PricedProduct, PricingContext,
  QuoteLine, SelectionMap, Tier, Totals, BillingFrequency } from './types.js'

// ---------------------------------------------------------------------------
// Buy price
// ---------------------------------------------------------------------------

/**
 * Effective discount off list = product base discount x tier multiplier, clamped to 0–100%.
 *
 * A channel partner with NO tier is a data error (the DB requires one), so this
 * fails safe with zero discount rather than handing out the full published
 * discount — a partner quoting at list is a phone call, a partner silently
 * buying at 20% off is a commercial leak nobody notices.
 */
/**
 * Decision 207 (Mark, 2026-09-09): the quantity ceiling. Was 9999.99 — "where
 * the document layout is sized to" — and a real order needed more (per-seat
 * and per-unit lines run into five and six figures). The column stores
 * numeric(12,2), so this is a code-and-layout limit only: 999,999.99, still
 * to 2 dp (Mark: "we sell 9.9 but never 9.99" — 2 dp costs nothing here and
 * keeps every existing quantity as it is).
 */
export const QTY_MAX = 999_999.99

export function effectiveDiscount(baseDiscountPct: number, tier: Tier | null): number {
  if (!tier) return 0
  const base = Number.isFinite(baseDiscountPct) ? baseDiscountPct : 0
  const raw = base * tier.multiplier
  return Math.min(Math.max(raw, 0), 1)
}

/**
 * The cost basis for a unit — what "margin" is measured against.
 *
 *   agent / reseller -> what Wave9 invoices the PARTNER: list less their
 *                       tier-scaled discount.
 *   internal         -> Wave9's own COST (decision 53), because internal mode is
 *                       Wave9 selling directly. Using list here — as this did
 *                       until 2026-07-31 — made every internal quote show zero
 *                       margin, which is worse than useless: it is wrong.
 *
 * ⚠️ **Zero is a REAL cost, not a missing one** (decision 54) — Wave9's own
 * services cost Wave9 nothing to supply, so £0 is the correct figure and 100%
 * margin is the correct answer.
 *
 * An ABSENT cost (`null` in HubSpot) is treated as £0 too — **amended
 * 2026-08-05 (Mark), reversing the list-fallback half of #53**: most costless
 * products ARE Wave9's own services, so showing 0% margin on them was simply
 * wrong, and a wrong-looking number gets ignored rather than fixed. The UI
 * badges these lines via `costMissing()` so the 100% reads as indicative, not
 * as fact.
 */
export function buyPrice(product: CatalogueProduct, ctx: PricingContext): number {
  if (ctx.mode === 'internal') {
    const cost = product.costPrice
    // `>= 0` not `> 0`: a genuine zero-cost product must keep its zero.
    // Absent (null) or nonsense (negative) -> £0, badged via costMissing().
    return round2(cost != null && cost >= 0 ? cost : 0)
  }
  return round2(product.listPrice * (1 - effectiveDiscount(product.baseDiscountPct, ctx.tier)))
}

/**
 * True when HubSpot holds NO cost for this product, so internal margin is a
 * fallback rather than a fact.
 *
 * ⚠️ A cost of £0 is not missing — Wave9's own services genuinely cost nothing
 * to supply. Only null (or a nonsense negative) counts as unknown.
 */
export function costMissing(product: Pick<CatalogueProduct, 'costPrice'>): boolean {
  return product.costPrice == null || product.costPrice < 0
}

/** Lowest legal sell price for a mode. null = unconstrained. */
export function sellFloor(mode: PartnerMode, prices: { listPrice: number; buyPrice: number }): number | null {
  switch (mode) {
    case 'agent':    return round2(prices.listPrice)   // locked, floor == ceiling
    case 'reseller': return round2(prices.buyPrice)    // no negative margin
    case 'internal': return null                       // trusted staff
  }
}

export function isSellLocked(mode: PartnerMode): boolean {
  return mode === 'agent'
}

/** Resolve one catalogue product against a partner's tier and mode. */
export function priceProduct(product: CatalogueProduct, ctx: PricingContext): PricedProduct {
  const buy = buyPrice(product, ctx)
  const list = round2(product.listPrice)
  return {
    ...product,
    listPrice: list,
    buyPrice: buy,
    effectiveDiscountPct: ctx.mode === 'internal' ? 0 : effectiveDiscount(product.baseDiscountPct, ctx.tier),
    // Resellers start at list and discount down towards their buy price; agents
    // and internal staff start at list too.
    defaultSellPrice: list,
    sellFloor: sellFloor(ctx.mode, { listPrice: list, buyPrice: buy }),
    sellLocked: isSellLocked(ctx.mode),
  }
}

export function priceCatalogue(products: CatalogueProduct[], ctx: PricingContext): PricedProduct[] {
  return products.map((p) => priceProduct(p, ctx))
}

/** What a partner is allowed to see. Agents never see buy prices (they don't buy). */
export function visibleCatalogue(products: CatalogueProduct[], mode: PartnerMode): CatalogueProduct[] {
  if (mode === 'internal') return products.filter((p) => p.active)
  return products.filter((p) => p.active && p.resellerVisible)
}

// ---------------------------------------------------------------------------
// Sell price validation
// ---------------------------------------------------------------------------

export interface SellPriceCheck {
  ok: boolean
  /** Corrected price to use when ok === false. */
  corrected: number
  reason?: string
}

/**
 * Validate a user-entered sell price for the mode. Called on every keystroke in
 * the builder AND server-side before persisting — the UI is a convenience, the
 * API is the guardrail (there is no approvals flow; pricing rules ARE the control).
 */
export function checkSellPrice(
  mode: PartnerMode,
  prices: { listPrice: number; buyPrice: number; sellPrice: number },
): SellPriceCheck {
  const sell = round2(prices.sellPrice)
  const list = round2(prices.listPrice)
  const buy = round2(prices.buyPrice)

  if (!Number.isFinite(sell) || sell < 0) {
    return { ok: false, corrected: mode === 'agent' ? list : buy, reason: 'Price must be a positive amount.' }
  }
  if (mode === 'agent') {
    return sell === list
      ? { ok: true, corrected: sell }
      : { ok: false, corrected: list, reason: 'Agent quotes are list-priced — sell price cannot be changed.' }
  }
  if (mode === 'reseller' && sell < buy) {
    return { ok: false, corrected: buy, reason: 'Sell price cannot go below your buy price.' }
  }
  return { ok: true, corrected: sell }
}

/** Margin on a single unit or line. marginPct is of the SELL price (gross margin). */
export function margin(sell: number, buy: number): { marginAmount: number; marginPct: number } {
  const amount = round2(sell - buy)
  const pct = sell > 0 ? amount / sell : 0
  return { marginAmount: amount, marginPct: pct }
}

/**
 * The sell price that yields a DESIRED margin on a cost (decision 154) — the
 * inverse of `margin()`, so the two always agree: margin is of the SELL price
 * (gross margin), hence sell = cost ÷ (1 − margin).
 *
 *   sellForMargin(600, 0.40) -> 1000    (£1,000 at 40% margin costs £600)
 *
 * Returns null when the maths has no useful answer: a non-finite or negative
 * cost, a margin outside [0, 1), or a £0 cost — at £0 EVERY sell price is 100%
 * margin, so there is nothing to calculate and the UI says so instead.
 */
export function sellForMargin(cost: number, marginPct: number): number | null {
  if (!Number.isFinite(cost) || cost <= 0) return null
  if (!Number.isFinite(marginPct) || marginPct < 0 || marginPct >= 1) return null
  return round2(cost / (1 - marginPct))
}

// ---------------------------------------------------------------------------
// Quantities (decision 36 — customer-adjustable within bounds)
// ---------------------------------------------------------------------------

/**
 * Clamp a customer-requested quantity into the line's bounds. Non-editable
 * lines keep their qty.
 *
 * Decimal quantities to 2dp are legal (decision 79, 2026-08-05 — e.g. 2.5 days
 * of engineering; the DB column was always numeric(12,2)). Rounding, not
 * flooring: 2.5 must stay 2.5, and a customer stepping ±1 from it lands on
 * 3.5, still inside the bounds clamp.
 */
export function clampQty(line: QuoteLine, requested: number | undefined): number {
  if (!line.qtyEditable || requested == null || !Number.isFinite(requested)) return line.qty
  const min = line.qtyMin ?? 0
  const max = line.qtyMax ?? Number.POSITIVE_INFINITY
  return Math.min(Math.max(round2(requested), min), max)
}

// ---------------------------------------------------------------------------
// Selection resolution (required / optional / choose-one)
// ---------------------------------------------------------------------------

/**
 * Work out which lines are actually on the quote given the customer's choices.
 *
 *   required -> always in
 *   optional -> in when ticked (defaults to `preselected`)
 *   option   -> member of a choose_one group: the customer's pick, else the
 *               preselected member. A group with NO preselected member is
 *               optional — it can resolve to NONE (decision 107).
 */
// ---------------------------------------------------------------------------
// Optional sections (decision 87, 2026-08-06) — a WHOLE section can be customer
// selectable. The section's inclusion state travels in the same SelectionMap
// under a `s:<groupId>` key, so persistence, sanitising and the acceptance
// freeze all keep working unchanged. A deselected section removes every line
// in it — including lines inside its nested choose-one blocks.
// ---------------------------------------------------------------------------

/** The group facts selection resolution needs — a subset of the DB row. */
export interface SelectionGroup {
  id: string
  kind: 'section' | 'choose_one'
  parentGroupId?: string | null
  optional?: boolean
  /** Optional sections only: whether the section starts INCLUDED. Default true.
   *  Choice-set members: whether this section is the set's DEFAULT pick (117). */
  preselected?: boolean
  /** Decision 117: sections sharing a key are ALTERNATIVES — the customer
   *  picks one (or none, when no member is preselected). Null = standalone. */
  choiceSet?: string | null
  /** Ordering inside a choice set (the DB sort) — first-by-sort breaks ties. */
  sort?: number
}

/** The SelectionMap key carrying a section's include/exclude state. */
export const sectionKey = (groupId: string) => `s:${groupId}`

/**
 * The set of INCLUDED section ids under the current selections (decision 117
 * pulled this into one place — every surface and the totals maths share it).
 *
 *   plain section     -> always included
 *   optional section  -> its s:<id> switch, else `preselected` (default true)
 *   choice-set member -> the customer's explicitly-selected member wins (first
 *                        by sort if several claim it), else the preselected
 *                        member, else NONE — the 107 semantics at section scope.
 */
export function includedSectionIds(
  groups: SelectionGroup[], selections: SelectionMap = {},
): Set<string> {
  const included = new Set<string>()
  const bySet = new Map<string, SelectionGroup[]>()
  for (const g of groups) {
    if (g.kind !== 'section') continue
    if (g.choiceSet) {
      const list = bySet.get(g.choiceSet) ?? []
      list.push(g)
      bySet.set(g.choiceSet, list)
      continue
    }
    if (!g.optional) { included.add(g.id); continue }
    if (selections[sectionKey(g.id)]?.selected ?? (g.preselected ?? true)) included.add(g.id)
  }
  for (const members of bySet.values()) {
    // EXACTLY the line-level choose-one rule (107): the customer's explicit
    // pick, else the preselected member, else none. A set WITH a default can
    // never resolve to none; a defaultless set can.
    const sorted = [...members].sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0))
    const picked = sorted.find((m) => selections[sectionKey(m.id)]?.selected === true)
      ?? sorted.find((m) => m.preselected)
    if (picked) included.add(picked.id)
  }
  return included
}

/** Is this section currently included? (Thin wrapper over includedSectionIds
 *  for single-group callers — choice-set logic needs the WHOLE group list.) */
export function sectionIncluded(
  group: SelectionGroup, selections: SelectionMap = {}, allGroups?: SelectionGroup[],
): boolean {
  if (group.choiceSet && allGroups) return includedSectionIds(allGroups, selections).has(group.id)
  if (group.choiceSet) return selections[sectionKey(group.id)]?.selected ?? (group.preselected ?? false)
  if (!group.optional) return true
  return selections[sectionKey(group.id)]?.selected ?? (group.preselected ?? true)
}

/** The SECTION a line sits in — a line in a choose-one block belongs to the block's parent. */
function sectionIdFor(line: QuoteLine, byId: Map<string, SelectionGroup>): string | null {
  if (!line.groupId) return null
  const g = byId.get(line.groupId)
  if (!g) return null
  return g.kind === 'choose_one' ? (g.parentGroupId ?? null) : g.id
}

/** Drop every line belonging to a deselected optional section. */
export function filterIncludedLines(
  lines: QuoteLine[], selections: SelectionMap = {}, groups?: SelectionGroup[],
): QuoteLine[] {
  if (!groups || groups.length === 0) return lines
  const byId = new Map(groups.map((g) => [g.id, g]))
  const included = includedSectionIds(groups, selections)
  return lines.filter((line) => {
    const sid = sectionIdFor(line, byId)
    if (!sid) return true
    return !byId.get(sid) || included.has(sid)
  })
}

/** A section's presentation subtotal (decision 119) — SELL only, safe public. */
export interface SectionSubtotal {
  oneOffSell: number
  /** Recurring charges on a term of a year or more, normalised to a per-YEAR figure (monthly billing ×12). */
  annualSell: number
  /** Decision 253: recurring charges on a term SHORTER than a year, over that term. */
  termSell?: number
}

/**
 * Per-section subtotals over the CURRENT selections (decision 119 — "subtotal
 * pricing" sections show these instead of per-line prices). Lines in a nested
 * choose-one block roll into their parent section; unselected options and
 * excluded sections contribute nothing (an excluded section has NO entry).
 * Presentation only — computeTotals stays the one answer for quote totals.
 */
export function sectionSubtotals(
  lines: QuoteLine[], selections: SelectionMap = {}, groups: SelectionGroup[] = [],
): Map<string, SectionSubtotal> {
  const byId = new Map(groups.map((g) => [g.id, g]))
  const out = new Map<string, SectionSubtotal>()
  for (const line of applySelections(lines, selections, groups)) {
    const sid = sectionIdFor(line, byId)
    if (!sid) continue
    const cur = out.get(sid) ?? { oneOffSell: 0, annualSell: 0 }
    const sell = lineTotal(line.sellPrice, line.qty)
    const years = termYears(line)
    if (line.recurring && years < 1) cur.termSell = round2((cur.termSell ?? 0) + annualised(sell, line) * years)
    else if (line.recurring) cur.annualSell = round2(cur.annualSell + annualised(sell, line))
    else cur.oneOffSell = round2(cur.oneOffSell + sell)
    out.set(sid, cur)
  }
  return out
}

export function resolveSelectedLines(
  lines: QuoteLine[], selections: SelectionMap = {}, groups?: SelectionGroup[],
): QuoteLine[] {
  lines = filterIncludedLines(lines, selections, groups)
  const chosenPerGroup = new Map<string, string>()

  for (const line of [...lines].sort((a, b) => a.sort - b.sort)) {
    if (line.optionality !== 'option' || !line.groupId) continue
    const picked = selections[line.id]?.selected
    if (picked && !chosenPerGroup.has(line.groupId)) chosenPerGroup.set(line.groupId, line.id)
  }
  // Fall back to the preselected member for any choose-one group with no pick.
  // Decision 107 (2026-08-08): NO preselected member means the group is
  // OPTIONAL — "none" is a valid resolution, so there is no first-member
  // fallback. A group with a default still resolves to exactly one.
  for (const line of [...lines].sort((a, b) => a.sort - b.sort)) {
    if (line.optionality !== 'option' || !line.groupId) continue
    if (chosenPerGroup.has(line.groupId)) continue
    const members = lines.filter((l) => l.groupId === line.groupId && l.optionality === 'option')
                         .sort((a, b) => a.sort - b.sort)
    const fallback = members.find((m) => m.preselected)
    if (fallback) chosenPerGroup.set(line.groupId, fallback.id)
  }

  return lines.filter((line) => {
    switch (line.optionality) {
      case 'required': return true
      case 'optional': return selections[line.id]?.selected ?? line.preselected
      case 'option':   return line.groupId ? chosenPerGroup.get(line.groupId) === line.id : false
    }
  })
}

/** Apply customer quantity choices to the selected lines. */
export function applySelections(
  lines: QuoteLine[], selections: SelectionMap = {}, groups?: SelectionGroup[],
): QuoteLine[] {
  return resolveSelectedLines(lines, selections, groups)
    .map((line) => ({ ...line, qty: clampQty(line, selections[line.id]?.qty) }))
}

// ---------------------------------------------------------------------------
// Totals (decision 31 — recurring bills ANNUALLY, terms 1 / 3 / 5 years;
//         decision 253 — any whole-month term, quarterly / six-monthly / monthly billing)
// ---------------------------------------------------------------------------

/**
 * A line's term as a (possibly fractional) number of years — 36 → 3, 3 → 0.25 —
 * defaulting to 1 for recurring lines with no term set. Decision 253: terms are
 * whole months, so this is no longer rounded; 12 / 36 / 60 compute as before.
 */
export function termYears(line: Pick<QuoteLine, 'recurring' | 'termMonths'>): number {
  if (!line.recurring) return 0
  const months = line.termMonths && line.termMonths > 0 ? line.termMonths : 12
  return months / 12
}

/** "3 yr" · "18 mo" · "3 mo" — a term for a label; null / 0 → null. */
export function termLabel(termMonths: number | null | undefined): string | null {
  if (!termMonths || termMonths <= 0) return null
  return termMonths % 12 === 0 ? `${termMonths / 12} yr` : `${termMonths} mo`
}

/** Billings per year by frequency (decision 253). Unknown / absent = annual. */
export const BILLINGS_PER_YEAR: Record<BillingFrequency, number> = {
  annually: 1, per_six_months: 2, quarterly: 4, monthly: 12, one_time: 0,
}

/** Short per-period suffix for a unit price: "/mo" · "/qtr" · "/6 mo" · "/yr". */
export const FREQUENCY_SUFFIX: Record<BillingFrequency, string> = {
  annually: '/yr', per_six_months: '/6 mo', quarterly: '/qtr', monthly: '/mo', one_time: '',
}

/**
 * HubSpot's `recurringbillingfrequency` (or a stored value) → a quote frequency.
 * Blank = one-off. A recurring frequency the quote model does not carry
 * (per_two_years …) reads as annual, which is what every such product was
 * before decision 253 — callers that care can check `isQuoteFrequency` first.
 */
export function parseQuoteFrequency(value: string | null | undefined, recurring = true): BillingFrequency {
  const v = String(value ?? '').trim().toLowerCase()
  if (isQuoteFrequency(v)) return v
  if (v === '' || v === 'onetime' || v === 'one-time') return recurring ? 'annually' : 'one_time'
  if (v === 'yearly' || v === 'annual') return 'annually'
  if (v === 'semiannually' || v === 'semi_annually') return 'per_six_months'
  return 'annually'
}

export function isQuoteFrequency(v: unknown): v is BillingFrequency {
  return v === 'annually' || v === 'per_six_months' || v === 'quarterly' || v === 'monthly' || v === 'one_time'
}

/**
 * How many times a recurring line is billed per year.
 *
 * ⚠️ Decision 31 says Wave9 bills annually, and nearly every product in the price
 * book does — but "nearly every" is not "always". A monthly price run through
 * annual maths understates the contract by a factor of 12, which is the kind of
 * error that reaches an invoice before anyone notices. Decision 253 added
 * quarterly and six-monthly the same way. Absent / one_time on a recurring line
 * reads as annual.
 */
export function billingsPerYear(line: Pick<QuoteLine, 'billingFrequency'>): number {
  const f = line.billingFrequency
  return f && f !== 'one_time' ? BILLINGS_PER_YEAR[f] : 1
}

/** A recurring line's price expressed per YEAR, whatever HubSpot bills it at. */
export function annualised(unitPrice: number, line: Pick<QuoteLine, 'billingFrequency'>): number {
  return round2(unitPrice * billingsPerYear(line))
}

/**
 * Totals for a set of lines. Selections are applied first, so this is safe to
 * call straight from the customer page on every click.
 *
 * One-off and annual recurring are kept apart deliberately — a school's finance
 * team reads them as different budget lines, and mixing them is how ARR rollups
 * go wrong. Contract value = one-off + (annual x years) over the longest term.
 */
export function computeTotals(
  lines: QuoteLine[], selections: SelectionMap = {}, groups?: SelectionGroup[],
): Totals {
  const selected = applySelections(lines, selections, groups)

  let oneOffSell = 0, annualSell = 0, contractValueSell = 0
  let oneOffBuy = 0, annualBuy = 0, contractValueBuy = 0
  let termMonths = 0
  let hasSurveyLines = false

  for (const line of selected) {
    const sell = lineTotal(line.sellPrice, line.qty)
    const buy = lineTotal(line.buyPrice, line.qty)
    if (line.subjectToSurvey) hasSurveyLines = true

    if (line.recurring) {
      const years = termYears(line)
      // Normalise to a per-year figure first — a monthly-billed line is 12 of them.
      const perYearSell = annualised(sell, line)
      const perYearBuy = annualised(buy, line)
      // Decision 253: a term shorter than a year is not an annual run-rate —
      // it counts in contract value only.
      if (years >= 1) {
        annualSell += perYearSell
        annualBuy += perYearBuy
      }
      contractValueSell += round2(perYearSell * years)
      contractValueBuy += round2(perYearBuy * years)
      termMonths = Math.max(termMonths, line.termMonths ?? 12)
    } else {
      oneOffSell += sell
      oneOffBuy += buy
      contractValueSell += sell
      contractValueBuy += buy
    }
  }

  const m = margin(round2(contractValueSell), round2(contractValueBuy))
  return {
    oneOffSell: round2(oneOffSell),
    annualSell: round2(annualSell),
    contractValueSell: round2(contractValueSell),
    oneOffBuy: round2(oneOffBuy),
    annualBuy: round2(annualBuy),
    contractValueBuy: round2(contractValueBuy),
    termMonths,
    marginAmount: m.marginAmount,
    marginPct: m.marginPct,
    lineCount: selected.length,
    hasSurveyLines,
  }
}

/** Public-facing totals only. ⚠️ Use this for anything the end customer can see. */
export function publicTotals(t: Totals) {
  const { oneOffSell, annualSell, contractValueSell, termMonths, lineCount, hasSurveyLines } = t
  return { oneOffSell, annualSell, contractValueSell, termMonths, lineCount, hasSurveyLines }
}

// ---------------------------------------------------------------------------
// HubSpot write-side pricing (decisions 4, 19, 29)
// ---------------------------------------------------------------------------

/**
 * The unit price a line is written to HubSpot at — this is what the Hub's order
 * book and ARR rollups consume, so it is Wave9 revenue, not the customer price.
 *
 *   reseller -> buy price  (Wave9 invoices the reseller)
 *   agent    -> LIST price (Wave9 invoices the end user; the referral fee is a
 *               separate deal field and is never netted off — decision 19)
 *   internal -> sell price (Wave9 invoices the end user directly)
 */
export function hubspotUnitPrice(mode: PartnerMode, line: QuoteLine): number {
  switch (mode) {
    case 'reseller': return round2(line.buyPrice)
    case 'agent':    return round2(line.listPrice)
    case 'internal': return round2(line.sellPrice)
  }
}

/** HubSpot recurring billing frequency — what the product actually is (decisions 31, 253). */
export function hubspotBillingFrequency(line: QuoteLine): BillingFrequency {
  if (!line.recurring) return 'one_time'
  // Echo back what the product actually is, rather than forcing annual — the
  // Hub's ARR rollups read this field and a lie here mis-counts revenue.
  const f = line.billingFrequency
  return f && f !== 'one_time' ? f : 'annually'
}

/** Total deal amount as HubSpot will see it, for pre-send reconciliation. */
export function hubspotDealAmount(
  mode: PartnerMode, lines: QuoteLine[], selections: SelectionMap = {}, groups?: SelectionGroup[],
): number {
  return round2(
    applySelections(lines, selections, groups).reduce((sum, line) => {
      const unit = hubspotUnitPrice(mode, line)
      const years = line.recurring ? termYears(line) : 1
      const perPeriod = lineTotal(unit, line.qty)
      const perYear = line.recurring ? annualised(perPeriod, line) : perPeriod
      return sum + perYear * years
    }, 0),
  )
}

// ---------------------------------------------------------------------------
// Referral fee (decisions 18, 19)
// ---------------------------------------------------------------------------

/**
 * The one-off referral fee owed on an accepted quote. Agent mode only — a fee on
 * a reseller or internal quote is always a bug, never a business case.
 */
export function referralFeeFor(
  partner: Pick<Partner, 'mode' | 'referralFee'>,
  quote: { referralFeeOverride?: number | null },
): number {
  if (partner.mode !== 'agent') return 0
  const fee = quote.referralFeeOverride ?? partner.referralFee ?? 0
  return round2(Math.max(0, fee))
}

// ---------------------------------------------------------------------------
// Quote numbering (decision 13)
// ---------------------------------------------------------------------------

/** e.g. quoteNumber('ACME', 2026, 42) -> 'ACME-2026-0042' */
export function quoteNumber(prefix: string, year: number, seq: number): string {
  const clean = prefix.trim().toUpperCase().replace(/[^A-Z0-9]/g, '')
  return `${clean}-${year}-${String(seq).padStart(4, '0')}`
}

// ---------------------------------------------------------------------------
// Ad-hoc lines (decision 29 — internal only)
// ---------------------------------------------------------------------------

export function canAddAdhocLines(mode: PartnerMode): boolean {
  return mode === 'internal'
}

/** Partners are catalogue-only so the tier-pricing guardrails always hold. */
export function assertAdhocAllowed(mode: PartnerMode): void {
  if (!canAddAdhocLines(mode)) {
    throw new Error('Ad-hoc line items are internal-mode only (decision 29).')
  }
}
